import { useEffect, useMemo, useState } from "react";
import {
    useGetAdminConsentItems,
    useGetAdminConsentItemVersions,
    useGetAdminConsentRecords,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import type {
    AdminConsentRecordDecisionFilter,
    AdminConsentRecordSourceFilter,
    AdminConsentRecordTypeFilter,
} from "../consentGovernanceTypes";

const DEFAULT_ROWS_PER_PAGE = 25;
const RECORD_SEARCH_DEBOUNCE_MS = 300;

type UseConsentGovernanceDataInput = {
    isConsentGovernanceEnabled: boolean;
    selectedLocale: string;
    uiLocale: string;
};

export const useConsentGovernanceData = ({
    isConsentGovernanceEnabled,
    selectedLocale,
    uiLocale,
}: UseConsentGovernanceDataInput) => {
    const [documentsPage, setDocumentsPage] = useState(0);
    const [documentsRowsPerPage, setDocumentsRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [optInsPage, setOptInsPage] = useState(0);
    const [optInsRowsPerPage, setOptInsRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [versionsPage, setVersionsPage] = useState(0);
    const [versionsRowsPerPage, setVersionsRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [versionPurposeFilter, setVersionPurposeFilter] = useState<number | null>(null);
    const [recordSearch, setRecordSearch] = useState("");
    const [debouncedRecordSearch, setDebouncedRecordSearch] = useState("");
    const [recordDecisionFilter, setRecordDecisionFilter] = useState<AdminConsentRecordDecisionFilter>("all");
    const [recordSourceFilter, setRecordSourceFilter] = useState<AdminConsentRecordSourceFilter>("all");
    const [recordTypeFilter, setRecordTypeFilter] = useState<AdminConsentRecordTypeFilter>("all");
    const [recordsPage, setRecordsPage] = useState(0);
    const [recordsRowsPerPage, setRecordsRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const recordsQueryParams = useMemo(() => {
        const params: Record<string, string> = {
            "order[decidedAt]": "desc",
            "order[id]": "desc",
        };
        const search = debouncedRecordSearch.trim();

        if (recordDecisionFilter !== "all") {
            params.decision = recordDecisionFilter;
        }

        if (recordSourceFilter !== "all") {
            params.source = recordSourceFilter;
        }

        if (recordTypeFilter !== "all") {
            params.type = recordTypeFilter;
        }

        if (search) {
            params.search = search;
        }

        return params;
    }, [debouncedRecordSearch, recordDecisionFilter, recordSourceFilter, recordTypeFilter]);

    const {
        consentItems: documents = [],
        totalItems: totalDocuments = 0,
        isLoading: isDocumentsLoading,
    } = useGetAdminConsentItems({
        enabled: isConsentGovernanceEnabled,
        itemsPerPage: documentsRowsPerPage,
        locale: selectedLocale,
        page: documentsPage + 1,
        queryParams: {
            "order[id]": "asc",
            "order[sortOrder]": "asc",
            type: "document",
        },
    });

    const {
        consentItems: optIns = [],
        totalItems: totalOptIns = 0,
        isLoading: isOptInsLoading,
    } = useGetAdminConsentItems({
        enabled: isConsentGovernanceEnabled,
        itemsPerPage: optInsRowsPerPage,
        locale: selectedLocale,
        page: optInsPage + 1,
        queryParams: {
            "order[id]": "asc",
            "order[sortOrder]": "asc",
            type: "opt_in",
        },
    });

    const { consentItems = [] } = useGetAdminConsentItems({
        enabled: isConsentGovernanceEnabled,
        fetchAllPages: true,
        itemsPerPage: 200,
        locale: selectedLocale,
        queryParams: {
            "order[id]": "asc",
            "order[sortOrder]": "asc",
        },
    });

    const {
        consentItemVersions = [],
        totalItems: totalConsentItemVersions = 0,
        isLoading: isConsentItemVersionsLoading,
    } = useGetAdminConsentItemVersions({
        enabled: isConsentGovernanceEnabled,
        itemsPerPage: versionsRowsPerPage,
        locale: selectedLocale,
        page: versionsPage + 1,
        queryParams: {
            "consentItem.id": versionPurposeFilter ?? undefined,
            "order[id]": "desc",
            "order[publishedAt]": "desc",
        },
    });

    const { consentItemVersions: latestConsentItemVersions = [] } = useGetAdminConsentItemVersions({
        enabled: isConsentGovernanceEnabled,
        fetchAllPages: true,
        itemsPerPage: 200,
        locale: selectedLocale,
        queryParams: {
            "order[id]": "desc",
            "order[publishedAt]": "desc",
        },
    });

    const {
        consentRecords: pagedConsentRecords = [],
        totalItems: totalConsentRecords = 0,
        isLoading: isPagedConsentRecordsLoading,
    } = useGetAdminConsentRecords({
        enabled: isConsentGovernanceEnabled,
        itemsPerPage: recordsRowsPerPage,
        locale: uiLocale,
        page: recordsPage + 1,
        queryParams: recordsQueryParams,
    });

    const filteredRecords = pagedConsentRecords;
    const recordPaginationTotal = totalConsentRecords;
    const paginatedRecords = pagedConsentRecords;

    useEffect(() => {
        setDocumentsPage(0);
    }, [documentsRowsPerPage, selectedLocale]);

    useEffect(() => {
        setOptInsPage(0);
    }, [optInsRowsPerPage, selectedLocale]);

    useEffect(() => {
        setVersionsPage(0);
    }, [selectedLocale, versionPurposeFilter, versionsRowsPerPage]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedRecordSearch(recordSearch);
        }, RECORD_SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [recordSearch]);

    useEffect(() => {
        setRecordsPage(0);
    }, [
        debouncedRecordSearch,
        recordDecisionFilter,
        recordSourceFilter,
        recordsRowsPerPage,
        recordTypeFilter,
        uiLocale,
    ]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(totalDocuments / documentsRowsPerPage) - 1);

        if (documentsPage > maxPage) {
            setDocumentsPage(maxPage);
        }
    }, [documentsPage, documentsRowsPerPage, totalDocuments]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(totalOptIns / optInsRowsPerPage) - 1);

        if (optInsPage > maxPage) {
            setOptInsPage(maxPage);
        }
    }, [optInsPage, optInsRowsPerPage, totalOptIns]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(totalConsentItemVersions / versionsRowsPerPage) - 1);

        if (versionsPage > maxPage) {
            setVersionsPage(maxPage);
        }
    }, [totalConsentItemVersions, versionsPage, versionsRowsPerPage]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(recordPaginationTotal / recordsRowsPerPage) - 1);

        if (recordsPage > maxPage) {
            setRecordsPage(maxPage);
        }
    }, [recordPaginationTotal, recordsPage, recordsRowsPerPage]);

    const handleDocumentsRowsPerPageChange = (rowsPerPage: number) => {
        setDocumentsRowsPerPage(rowsPerPage);
        setDocumentsPage(0);
    };

    const handleOptInsRowsPerPageChange = (rowsPerPage: number) => {
        setOptInsRowsPerPage(rowsPerPage);
        setOptInsPage(0);
    };

    const handleVersionsRowsPerPageChange = (rowsPerPage: number) => {
        setVersionsRowsPerPage(rowsPerPage);
        setVersionsPage(0);
    };

    const handleRecordsRowsPerPageChange = (rowsPerPage: number) => {
        setRecordsRowsPerPage(rowsPerPage);
        setRecordsPage(0);
    };

    return {
        consentItemVersions,
        consentItems,
        consentRecords: filteredRecords,
        documents,
        documentsPage,
        documentsRowsPerPage,
        filteredRecords,
        handleDocumentsRowsPerPageChange,
        handleOptInsRowsPerPageChange,
        handleRecordsRowsPerPageChange,
        handleVersionsRowsPerPageChange,
        isConsentItemVersionsLoading,
        isDocumentsLoading,
        isOptInsLoading,
        isConsentRecordsLoading: isPagedConsentRecordsLoading,
        latestConsentItemVersions,
        optIns,
        optInsPage,
        optInsRowsPerPage,
        paginatedRecords,
        recordDecisionFilter,
        recordSearch,
        recordSourceFilter,
        recordTypeFilter,
        recordsPage,
        recordPaginationTotal,
        recordsRowsPerPage,
        setDocumentsPage,
        setOptInsPage,
        setRecordDecisionFilter,
        setRecordsPage,
        setRecordSearch,
        setRecordSourceFilter,
        setRecordTypeFilter,
        setVersionPurposeFilter,
        setVersionsPage,
        totalDocuments,
        totalConsentItemVersions,
        totalConsentRecords,
        totalOptIns,
        versionPurposeFilter,
        versionsPage,
        versionsRowsPerPage,
    };
};
